import React from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { ActivityLogPromiseClient } from '@trustero/trustero-api-web/lib/activity/activity_grpc_web_pb'
import {
  ActivityLogEntry,
  GetActivityLogRequest,
} from '@trustero/trustero-api-web/lib/activity/activity_pb'
import { getRpcAccess } from '../app/AppAuth/rpcAuthorization'
import { AuthAction, AuthRecord } from '../context/authContext'
import { authorizedGrpcClientFactory } from './grpcClient'

// How many entries one delta poll pulls. The poll runs every 60s, so this only
// needs to cover a burst of activity within that window.
const FEED_PAGE_SIZE = 1000

export type ActivityFeedCitation = {
  type: number
  id: string
}

export type ActivityFeedEntry = {
  createdAtMs: number
  actor: string
  // True for account-level changes (account settings, overview, other "general"
  // edits) that have no citable object of their own.
  isAccountChange: boolean
  citations: ActivityFeedCitation[]
}

const toMillis = (ts?: Timestamp): number => {
  if (!ts) return 0
  return ts.getSeconds() * 1000 + Math.floor(ts.getNanos() / 1e6)
}

/**
 * Reads the activity log as a change feed, used to invalidate client caches when
 * another actor changes something. Replaces the old log_entries-backed changelog:
 * activity log entries carry citations rather than a single subject, plus an
 * is_account_change flag for changes with no citable object.
 */
class ActivityFeedAdapter {
  static async getFeed(
    beginTimeMs: number,
    authCtx: AuthRecord | null,
    authDispatch: React.Dispatch<AuthAction> | null,
  ): Promise<ActivityFeedEntry[]> {
    if (
      getRpcAccess('/activity.ActivityLog/GetActivityLogs', authCtx) !==
      'allowed'
    )
      return []
    const client = authorizedGrpcClientFactory(
      ActivityLogPromiseClient,
      authCtx,
      authDispatch,
    )

    const request = new GetActivityLogRequest()
      .setEntriesPerPage(FEED_PAGE_SIZE)
      .setPageNumber(0)
    if (beginTimeMs > 0) {
      request.setBeginTime(Timestamp.fromDate(new Date(beginTimeMs)))
    }

    const response = await client.getActivityLogs(request)
    return (response.getActivityLogEntriesList() || []).map(
      (entry: ActivityLogEntry) => ({
        createdAtMs: toMillis(entry.getCreatedAt()),
        actor: entry.getActor(),
        isAccountChange: entry.getIsAccountChange(),
        citations: (entry.getCitationsList() || []).map((citation) => ({
          type: citation.getCitationType(),
          id: citation.getCitationId(),
        })),
      }),
    )
  }
}

export default ActivityFeedAdapter
